/**
 * What the Org Planning Overview may OFFER the person looking at it.
 *
 * The full audience (`internal_playground.view`) can propose under, edit and decide backfills for
 * anyone. A PROPOSER (`internal_playground.propose`, squad-org-planning-extended@finn.com) only
 * within their own reporting line — the Personio IDs the proxy sends as `viewer.lineEmployeeIds`.
 *
 * ⚠️ Cosmetic. Every one of those writes is re-checked by the proxy (`orgPlanningViewer.js`); this
 * exists so the page does not offer a button whose only outcome is a 403. It defaults to FULL
 * because an older proxy revision sends no `viewer`, and the proxy is what actually refuses.
 */
import { createContext, useContext } from 'react';
import type { OrgPlanningViewer } from '../../services/internalPlaygroundApi';

export interface ViewerScope {
  full: boolean;
  /** True for the full audience; for a proposer, whether `employeeId` is in their line. */
  inLine: (employeeId: string | null | undefined) => boolean;
}

export const FULL_VIEWER_SCOPE: ViewerScope = { full: true, inLine: () => true };

export function viewerScopeFrom(viewer: OrgPlanningViewer | undefined): ViewerScope {
  if (!viewer || viewer.scope !== 'line') return FULL_VIEWER_SCOPE;
  const ids = new Set(viewer.lineEmployeeIds ?? []);
  return { full: false, inLine: (employeeId) => Boolean(employeeId) && ids.has(String(employeeId)) };
}

export const ViewerScopeContext = createContext<ViewerScope>(FULL_VIEWER_SCOPE);

export function useViewerScope(): ViewerScope {
  return useContext(ViewerScopeContext);
}
